/**
 * Game Modal - Displays games in a fullscreen modal overlay
 */

import type { GameId, ModalState } from './types';
import { GAMES } from './types';

export class GameModal {
  private state: ModalState = {
    isOpen: false,
    currentGame: null,
  };

  private modalElement: HTMLDivElement | null = null;
  private iframeElement: HTMLIFrameElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private loadingElement: HTMLDivElement | null = null;

  constructor() {
    this.createModal();
    this.setupEventListeners();
  }

  private createModal(): void {
    // Create modal container
    this.modalElement = document.createElement('div');
    this.modalElement.id = 'game-modal';
    this.modalElement.className = 'game-modal';
    this.modalElement.innerHTML = `
      <div class="game-modal-backdrop"></div>
      <div class="game-modal-content">
        <button class="game-modal-close" aria-label="Close game">&times;</button>
        <div class="game-modal-loading">
          <div class="game-modal-spinner"></div>
          <p>Loading game...</p>
        </div>
        <iframe class="game-modal-iframe" frameborder="0" allow="autoplay; fullscreen"></iframe>
      </div>
    `;

    // Add styles
    this.addStyles();

    // Get references
    this.closeButton = this.modalElement.querySelector('.game-modal-close');
    this.iframeElement = this.modalElement.querySelector('.game-modal-iframe');
    this.loadingElement = this.modalElement.querySelector('.game-modal-loading');

    // Add to DOM
    document.body.appendChild(this.modalElement);
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .game-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        display: none;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .game-modal.open {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .game-modal.visible {
        opacity: 1;
      }

      .game-modal-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        cursor: pointer;
      }

      .game-modal-content {
        position: relative;
        width: 95%;
        height: 95%;
        max-width: 800px;
        max-height: 700px;
        background: #000;
        border: 4px solid #000;
        box-shadow: 0 0 0 4px #468D37, 15px 15px 0 0 rgba(0, 0, 0, 0.3);
        z-index: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
      }

      .game-modal-close {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 40px;
        height: 40px;
        background: #FF3BD2;
        color: white;
        border: 3px solid #000;
        border-radius: 0;
        font-size: 24px;
        font-weight: bold;
        cursor: pointer;
        z-index: 100;
        display: flex;
        justify-content: center;
        align-items: center;
        transition: transform 0.2s, background-color 0.2s;
        box-shadow: 3px 3px 0 #000;
      }

      .game-modal-close:hover {
        background: #8D6944;
        transform: translate(-2px, -2px);
        box-shadow: 5px 5px 0 #000;
      }

      .game-modal-close:active {
        transform: translate(1px, 1px);
        box-shadow: 2px 2px 0 #000;
      }

      .game-modal-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        color: white;
        font-family: 'Comic Neue', cursive;
        z-index: 5;
      }

      .game-modal-loading.hidden {
        display: none;
      }

      .game-modal-spinner {
        width: 50px;
        height: 50px;
        border: 4px solid rgba(255, 255, 255, 0.2);
        border-top-color: #FF3BD2;
        border-radius: 50%;
        animation: modal-spin 1s linear infinite;
        margin: 0 auto 15px;
      }

      @keyframes modal-spin {
        to { transform: rotate(360deg); }
      }

      .game-modal-iframe {
        width: 100%;
        height: 100%;
        border: none;
        background: #000;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .game-modal-iframe.loaded {
        opacity: 1;
      }

      /* Mobile adjustments */
      @media (max-width: 768px) {
        .game-modal-content {
          width: 100%;
          height: 100%;
          max-width: none;
          max-height: none;
          border: none;
          box-shadow: none;
        }

        .game-modal-close {
          top: 10px;
          right: 10px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  private setupEventListeners(): void {
    // Close button click
    this.closeButton?.addEventListener('click', () => this.close());

    // Backdrop click
    this.modalElement?.querySelector('.game-modal-backdrop')?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen) {
        this.close();
      }
    });

    // Iframe load
    this.iframeElement?.addEventListener('load', () => {
      this.loadingElement?.classList.add('hidden');
      this.iframeElement?.classList.add('loaded');
    });
  }

  /**
   * Open modal with specified game
   */
  open(gameId: GameId): void {
    const gameConfig = GAMES.find(g => g.id === gameId);
    if (!gameConfig || gameConfig.status !== 'live') {
      console.warn(`Game ${gameId} not found or not available`);
      return;
    }

    this.state.isOpen = true;
    this.state.currentGame = gameId;

    // Reset iframe state
    this.iframeElement?.classList.remove('loaded');
    this.loadingElement?.classList.remove('hidden');

    // Show modal
    this.modalElement?.classList.add('open');

    // Trigger animation
    requestAnimationFrame(() => {
      this.modalElement?.classList.add('visible');
    });

    // Load game
    if (this.iframeElement) {
      this.iframeElement.src = gameConfig.entryUrl;
    }

    // Disable body scroll
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close modal
   */
  close(): void {
    if (!this.state.isOpen) return;

    this.state.isOpen = false;
    this.state.currentGame = null;

    // Hide modal with animation
    this.modalElement?.classList.remove('visible');

    // Wait for animation then hide
    setTimeout(() => {
      this.modalElement?.classList.remove('open');

      // Unload iframe
      if (this.iframeElement) {
        this.iframeElement.src = 'about:blank';
        this.iframeElement.classList.remove('loaded');
      }
    }, 300);

    // Re-enable body scroll
    document.body.style.overflow = '';
  }

  /**
   * Check if modal is open
   */
  isOpen(): boolean {
    return this.state.isOpen;
  }

  /**
   * Get current game
   */
  getCurrentGame(): GameId | null {
    return this.state.currentGame;
  }
}
